import { useCallback, useEffect, useState } from "react";
import { Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "@/lib/api";
import { printHtml } from "@/components/staff/pos/printHtml";
import { fmtDiff } from "@/components/staff/ClosingHistory";

const t = (s) => (s ? new Date(s).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) : "–");
const dt = (s) => new Date(s).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const sel = "px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm";

const ClosingSlip = ({ d }) => (
  <div id="pos-closing-slip" className="pos-receipt" data-testid="pos-closing-slip">
    <div className="text-center"><div className="font-bold text-base tracking-widest">UPLINK COFFEE</div><div>TUTUP KASIR</div><div>Gerai {d.outlet || "Semua"}</div></div>
    <div className="rc-line" />
    <div className="rc-row"><span>Tanggal</span><span>{d.date}</span></div>
    <div className="rc-row"><span>Shift</span><span>{d.shift_label} ({t(d.start)}-{t(d.end)})</span></div>
    <div className="rc-row"><span>Transaksi pertama</span><span>{t(d.first_at)}</span></div>
    <div className="rc-row"><span>Transaksi terakhir</span><span>{t(d.last_at)}</span></div>
    <div className="rc-line" />
    <div className="rc-row"><span>Jumlah transaksi</span><span>{d.transactions}</span></div>
    <div className="rc-row"><span>· Makan di tempat</span><span>{d.dine_in}</span></div>
    <div className="rc-row"><span>· Bawa pulang</span><span>{d.takeaway}</span></div>
    <div className="rc-row"><span>Batal</span><span>{d.cancelled}</span></div>
    <div className="rc-row"><span>Item terjual</span><span>{d.items_sold}</span></div>
    <div className="rc-line" />
    <div className="rc-row"><span>Subtotal</span><span>{rupiah(d.subtotal)}</span></div>
    <div className="rc-row"><span>PB1</span><span>{rupiah(d.tax)}</span></div>
    <div className="rc-row font-bold text-sm"><span>TOTAL QRIS</span><span>{rupiah(d.total_qris)}</span></div>
    {d.by_cashier.length > 0 && <><div className="rc-line" />{d.by_cashier.map((c) => <div key={c.cashier} className="rc-row"><span>{c.cashier} ({c.count})</span><span>{rupiah(c.total)}</span></div>)}</>}
    <div className="rc-line" />
    {d.closed?.actual_qris != null && <><div className="rc-row"><span>QRIS masuk</span><span>{rupiah(d.closed.actual_qris)}</span></div><div className="rc-row font-bold"><span>SELISIH</span><span>{fmtDiff(d.closed.difference)}</span></div><div className="rc-line" /></>}
    {d.closed ? <div>Ditutup: {d.closed.closed_by}<br />{dt(d.closed.closed_at)}{d.closed.note && <><br />Catatan: {d.closed.note}</>}</div> : <div>Belum ditutup</div>}
    <div className="mt-1">Paraf kasir: ____________</div>
  </div>
);

export const PosClosing = ({ outlet }) => {
  const [q, setQ] = useState({ date: "", shift: "" });
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [actual, setActual] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/pos/closing", { params: { ...q, outlet } }).then((r) => { setD(r.data); setQ((x) => (x.date ? x : { date: r.data.date, shift: r.data.shift })); }).catch((e) => toast.error(errMsg(e)));
  }, [q, outlet]);
  useEffect(() => { load(); }, [load]);

  const close = async () => {
    if (actual === "") { toast.error("Isi nominal QRIS yang benar-benar masuk"); return; }
    const a = Number(actual);
    if (!window.confirm(`Tutup kasir shift ${d.shift_label} ${d.date}?\nSistem ${rupiah(d.total_qris)} · Masuk ${rupiah(a)} · Selisih ${fmtDiff(a - d.total_qris)}`)) return;
    setBusy(true);
    try { const r = await api.post("/pos/closing", { date: d.date, shift: d.shift, outlet, note, actual_qris: a }); setD(r.data); setActual(""); setNote(""); toast.success("Kasir ditutup"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="card-paper p-5 mt-6" data-testid="pos-closing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> Tutup kasir per shift</h3>
        <div className="flex flex-wrap gap-2">
          <input type="date" value={q.date} onChange={(e) => setQ((x) => ({ ...x, date: e.target.value }))} className={sel} data-testid="pos-closing-date" />
          <select value={q.shift} onChange={(e) => setQ((x) => ({ ...x, shift: e.target.value }))} className={sel} data-testid="pos-closing-shift">
            <option value="pagi">Pagi 07-15</option><option value="sore">Sore 15-23</option><option value="malam">Malam 23-07</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        {[["Transaksi", d.transactions, "pos-closing-count"], ["Total QRIS", rupiah(d.total_qris), "pos-closing-total"], ["PB1", rupiah(d.tax), "pos-closing-tax"], ["Item terjual", d.items_sold, "pos-closing-items"]].map(([l, v, id]) => (
          <div key={l} className="rounded-xl bg-sage-50 p-3"><div className="text-xs text-muted2">{l}</div><div className="font-bold text-xl text-forest" data-testid={id}>{v}</div></div>
        ))}
      </div>
      <div className="text-xs text-muted2 mt-2">Makan di tempat {d.dine_in} · Bawa pulang {d.takeaway} · Batal {d.cancelled}{d.by_cashier.length > 0 && ` · ${d.by_cashier.map((c) => `${c.cashier}: ${c.count}x`).join(", ")}`}</div>
      <div className="hidden"><ClosingSlip d={d} /></div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {d.closed ? <span className="text-sm font-semibold text-emerald-700" data-testid="pos-closing-done">Sudah ditutup oleh {d.closed.closed_by} · {dt(d.closed.closed_at)}{d.closed.actual_qris != null && ` · Selisih ${fmtDiff(d.closed.difference)}`}</span> : (
          <>
            <input type="number" min="0" inputMode="numeric" value={actual} onChange={(e) => setActual(e.target.value.replace(/\D/g, ""))} placeholder="QRIS masuk (sesuai mutasi)" className={`${sel} flex-1 min-w-[220px]`} data-testid="pos-closing-actual" />
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Catatan tutup kasir (opsional)" className={`${sel} flex-1 min-w-[180px]`} data-testid="pos-closing-note" />
            <button disabled={busy} onClick={close} className="btn-forest px-4 py-2 text-sm flex items-center gap-1.5" data-testid="pos-closing-submit"><Lock className="w-4 h-4" /> Tutup kasir</button>
          </>
        )}
        <button onClick={() => printHtml("pos-closing-slip")} className="px-4 py-2 rounded-full bg-sage-100 text-sm font-semibold flex items-center gap-1.5 ml-auto" data-testid="pos-closing-print"><Printer className="w-4 h-4" /> Cetak rekap</button>
      </div>
    </div>
  );
};
